
import { createTheme } from '@mui/material';
import alegreya from '../fonts/Alegreya/alegreya_regular.ttf';
import { utils, writeFile } from 'xlsx';

const DEBUG = false;

export const output = (message = '', title = '', force = false) => {
  if (DEBUG || force) {
    console.log(`_________________${title}_________________`);
    console.log(message);
    console.log("_________________END____________________");
  }
}

class FrenchNumbersToWords {
  french = 'fr'
  number = 0
  numberParts = []
  result = {
    parts: [],
    fullText: ''
  }
  units = {
    0: "zéro",
    1: "un",
    2: "deux",
    3: "trois",
    4: "quatre",
    5: "cinq",
    6: "six",
    7: "sept",
    8: "huit",
    9: "neuf",
    10: "dix",
    11: "onze",
    12: "douze",
    13: "treize",
    14: "quatorze",
    15: "quinze",
    16: "seize"
  }
  tens = {
    be: {
      10: "dix",
      20: "vingt",
      30: "trente",
      40: "quarante",
      50: "cinquante",
      60: "soixante",
      70: "septante",
      80: "huitante",
      90: "nonante"
    },
    fr: {
      10: "dix",
      20: "vingt",
      30: "trente",
      40: "quarante",
      50: "cinquante",
      60: "soixante",
      70: "soixante-dix",
      80: "quatre-vingt",
      90: "quatre-vingt-dix"
    }
  }
  // The units that we use depending on digits count in the number
  groups = [
    {
      single: '',
      plural: ''
    },
    {
      single: 'mille',
      plural: 'mille'
    },
    {
      single: 'million',
      plural: 'millions'
    }
  ]

  // We need the number and to define which version of French we are using
  constructor(number = 0, french = 'fr') {
    if (Object.keys(this.tens).indexOf(french) > -1) {
      this.french = french
      this.number = parseInt(number)
      this.numberAsWord = this.splitNumberPerLength()
    }
  }

  splitNumberPerLength() {
    // we will split the number to groups
    // each group has three digits. Ex: 12345 will have two groups: 12 (for thousands) and 345
    this.numberParts = this.number.toLocaleString("en").split(',')
    let n, full
    for (let j = 0; j < this.numberParts.length; j++) {

      n = parseInt(this.numberParts[j])
      full = {
        number: n,
        text: n > 1 ? this.hundred(n) : '',
        unit: this.getGroupNameByIndex(j)
      }
      this.result.parts.push(full)
    }
    this.result.fullText = this.generateFullText()
    return this.result
  }

  generateFullText() {
    let res = []
    for (var i = 0; i < this.result.parts.length; i++) {
      let txt = this.result.parts[i].text
      if (this.result.parts[i].unit) {
        if (txt.length) {
          txt += '-'
        }
        txt += this.result.parts[i].unit
      }
      if (txt.length) {
        res.push(txt)
      }
    }
    return res.join(' ')
  }

  /**
   * Due to the rules of French language, we convert the hundreds part and the tens part seperately
   * @param  {[number]} n the number we want to convert to a word
   * @return {[string]}    number being converted to a word
   */
  twoDigitsConverter(n = null) {
    const num = (n != null) ? n : this.number
    let result = ''
    if (num === '' && typeof num !== 'number') {
      return ''
    }
    if (num % 10 === 0 && num > 0) {
      // check in tens
      result = this.tens[this.french][num]
      // apply the plural format to 80
      if (num === 80) {
        result += 's'
      }
      return result
    } else if (this.units[num]) {
      return this.units[num]
    }
    // get the parts of the number, ex: 58 will be an array of [5, 8]
    const numberParts = num.toString().split('').map(Number)
    // get the number word from tens array, taking the french type in mind
    result = this.tens[this.french][`${numberParts[0]}0`] + '-'
    if (numberParts[1] === 1 && (numberParts[0] < 7)) {
      // if the number is less than 70
      result += 'et-' + this.units[numberParts[1]]
    } else if ((numberParts[0] < 7) || numberParts[0] === 8) {
      // in case the its is les than 70 or exactly 80
      result += this.units[numberParts[1]]
    } else if ((numberParts[0] === 7) || (numberParts[0] === 9)) {
      // in case of 70 and 90, we make a shift by -10 to the tens rank and shift of +10 to unit rank
      result = this.tens[this.french][`${parseInt(numberParts[0]) - 1}0`] + '-'
      if ((numberParts[0] === 7) && (numberParts[1] === 1)) {
        // 71
        result += 'et-'
      }
      result += this.twoDigitsConverter(parseInt(numberParts[1]) + 10)
    }
    return result
  }

  /**
   * Due to the rules of French language, we convert the hundreds part and the tens part seperately
   * @param  {[number]} n the number we want to convert to a word
   * @return {[string]}    number being converted to a word
   */
  hundred(n = null) {
    let result = ''
    const num = n != null ? n : this.number
    // calculate how many hundreds do we have in this number
    const hundreds = Math.floor(num / 100)
    // calculate the tens part of the number
    const rest = num % 100
    if (hundreds === 0) {
      // ex: we are converting the 12 in 12345
      // no need to worry about the cent unit, just convert the 12
      result = this.twoDigitsConverter(rest)
    } else {
      // here, we are converting a group of 3 digits
      result = 'cent'
      if (hundreds > 1) {
        result = this.twoDigitsConverter(hundreds) + '-' + result
      }

      // get the rest (in case we have it)
      if (rest > 0) {
        const restAsWord = this.twoDigitsConverter(rest)
        if (restAsWord) {
          if (rest === 1) {
            result += '-et'
          }
          result += '-' + restAsWord
        }
      }
    }
    return result
  }
  /**
   * Due to the rules of French language, we convert the hundreds part and the tens part seperately
   * @param  {[number]} groupIndex which group are we converting so we know the unit (nothing, mille, million etc)
   * @return {[string]}    unit of a number (nothing, mille, million etc)
   */
  getGroupNameByIndex(groupIndex) {
    const n = parseInt(this.numberParts[groupIndex]);
    const groupPos = this.numberParts.length - 1 - groupIndex;
    
    if (groupPos === 0) return ''; // Unités
    
    const group = this.groups[groupPos];
    if (!group) return '';

    // "mille" est invariable en français
    if (group.single === 'mille') return 'mille';
    
    return n > 1 ? group.plural : group.single;
  }
}


export const COLORS = {
  primary: "#0ea5e9", // Cyan moderne
  secondary: "#0f172a", // Navy profond
  tertiary: "#857f92", // Maintien de la compatibilité avec l'ancien thème (Mauve gris)
  accent: "#f59e0b", // Ambre pour les touches d'alerte/attention
  success: "#10b981",
  error: "#ef4444",
  warning: "#f59e0b",
  info: "#3b82f6",
  gray: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a"
  },
  background: {
    paper: "#ffffff",
    default: "#f8fafc"
  },
  text: {
    primary: "#0f172a",
    secondary: "#64748b",
    white: "#ffffff"
  }
}

export const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Alegreya", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 500 }
  },
  palette: {
    primary: {
      main: COLORS.primary,
      contrastText: '#fff'
    },
    secondary: {
      main: COLORS.secondary,
    },
    tertiary: {
      main: COLORS.tertiary,
      contrastText: '#fff'
    },
    background: {
      paper: COLORS.background.paper,
      default: COLORS.background.default
    },
    text: {
      primary: COLORS.text.primary,
      secondary: COLORS.text.secondary
    },
    grey: COLORS.gray,
    success: { main: COLORS.success },
    error: { main: COLORS.error },
    warning: { main: COLORS.warning },
    info: { main: COLORS.info }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          borderRadius: 12
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: `

            html {
              scroll-behavior: smooth;
            }
            body {
              background-color: #f8fafc;
            }
            @font-face {
              font-family: 'Alegreya';
              font-style: normal;
              font-display: swap;
              font-weight: 400;
              src: local('Alegreya'), url(${alegreya}) format('tff');
            }
          `,
    },
  }
});
export const priceFormatter = (price) => {
  if (price === null || price === undefined) return "0";
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(isNaN(price) ? 0 : price);
  // Remplacer tout caractère qui n'est pas un chiffre ou un signe moins par un espace
  return formatted.replace(/[^\d-]/g, ' ').trim();
}

export const priceFormatterV2 = (price) => {
  if (price === null || price === undefined) return "0";
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(isNaN(price) ? 0 : price);
  return formatted.replace(/[^\d-]/g, ' ').trim();
}

export const priceFormatterV3 = (price) => {
  if (price === null || price === undefined) return "0";
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(isNaN(price) ? 0 : price);
  return formatted.replace(/[^\d-]/g, ' ').trim();
}

// export const priceFormatterV3 = (price) => {
//   return (new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).formatToParts(
//     isNaN(price) ? 0 : price))
//     .map((part) => {
//       if (part.type == 'currency') {
//         part.value = "";
//       }
//       if (!(part.type == 'group' || part.type == "literal" || part.type == "")) {
//         return part.value;
//       }
//     }).join(" ");

// }


export const nameFormatter = (name) => {
  const underscoreRegex = new RegExp("_");
  const numeroRegex = new RegExp("numero");
  return name.replace(underscoreRegex, " ").replace(numeroRegex, "N°").replaceAll(/([A-Z])/g, ' $1').trim();
}


export const sortByProp = (a, b, prop) => {
  if (!a[prop] || !b[prop]) return 0;
  if (a[prop].toLowerCase() < b[prop].toLowerCase()) {
    return -1;
  }
  if (a[prop].toLowerCase() > b[prop].toLowerCase()) {
    return 1;
  }
  return 0;
}

const isValidDate = (date) => {
  if (Object.prototype.toString.call(date) === "[object Date]") {
    if (!isNaN(date.getTime())) {

      return true
    } else {
      return false;
    }
  } else {

    return false;
  }
}


export const dateFormatter = (str) => {
  if (!str || str === null || str === undefined || str === '' || str === '--') {
    return null;
  }

  // Si c'est déjà un objet Date
  if (Object.prototype.toString.call(str) === "[object Date]") {
    if (isValidDate(str)) return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(str);
    return null;
  }

  let d = null;

  // Analyser manuellement si ça ressemble à DD/MM/YYYY ou DD-MM-YYYY
  const frenchDatePattern = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/;
  const match = String(str).match(frenchDatePattern);

  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // 0-indexed
    const year = parseInt(match[3], 10);
    const fullYear = year < 100 ? 2000 + year : year;
    d = new Date(fullYear, month, day);
  } else {
    // Sinon on tente le parsing standard (ISO, etc.)
    d = new Date(str);
  }

  if (!isValidDate(d)) {
    // Si on arrive pas à parser, on retourne la chaîne originale au lieu de null
    // pour éviter que le texte disparaisse dans les tables
    return str;
  }

  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(d);
  } catch (e) {
    return str;
  }
}


export const jsonToXlsx = (json, title) => {
  const worksheet = utils.json_to_sheet(json);
  const workbook = utils.book_new();

  utils.book_append_sheet(workbook, worksheet, title);
  writeFile(workbook, `${title}.xlsx`, { compression: true });

}


export const calculateDays = (date, dateFin, cb) => {
  if (!date || !dateFin) {
    return 0;
  }

  // Utilisation de Date.parse pour plus de robustesse ou conversion explicite
  const d1 = new Date(date);
  const d2 = new Date(dateFin);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;

  // Remise à zéro des heures pour ne comparer que les jours
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);

  const Difference_In_Time = d2.getTime() - d1.getTime();
  const Difference_In_Days = Math.round(Difference_In_Time / (1000 * 3600 * 24));
  
  const result = (Difference_In_Days >= 0) ? Difference_In_Days : 0;
  cb?.();
  return result;
}

export const toEuro = (price) => (new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'EUR'

})
  .format(isNaN(price) ? 0 : (price / (655.957)))
  .replaceAll(",", " ")
  .replaceAll(".", ",")
  .split("€")
  .reverse()
  .join("") + " €"



);



// const nameFormatter = (name) => {
//   const underscoreRegex = new RegExp("_");
//   const numeroRegex = new RegExp("numero");
//   return name.replace(underscoreRegex, " ").replace(numeroRegex, "N°").replaceAll(/([A-Z])/g, ' $1').trim();
// }

export const calculateFrais = (frais) => {

  return frais.reduce((sum, current) => {

    return {
      montant: sum.montant + (current.quantite * current.prixUnitaire)
    }

  }, {
    montant: 0
  }).montant;

}


export const calculateFraisAsync = async (frais) => {

  let _frais = await frais.reduce((sum, current) => {

    return {
      montant: sum.montant + (current.quantite * current.prixUnitaire)
    }

  }, {
    montant: 0
  }).montant;

  return _frais;

}



export const toFrench = (arg) => {
  if (arg === 0) return "zéro";
  let result = new FrenchNumbersToWords(arg, 'fr').result;
  
  const parts = result.parts.filter(p => p.number > 0).map(p => {
    let text = p.text;
    let unit = p.unit;
    
    // Cas particulier pour 1
    if (p.number === 1) {
      if (unit === 'mille') {
        text = ''; // On dit "mille" et pas "un mille"
      } else if (unit === 'million' || unit === 'millions') {
        text = 'un';
        unit = 'million';
      } else if (unit === '') {
        text = 'un';
      }
    }
    
    return `${text} ${unit}`.trim();
  });

  return parts.join(' ').trim().toUpperCase();
}

export const numberToLetterFrench = toFrench;


// export const toFrench = (num) => {

//     if (num === 0) {
//       return 'zéro';
//     }

//     const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
//     const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
//     const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix'];
//     const hundreds = ['', 'cent', 'deux cents', 'trois cents', 'quatre cents', 'cinq cents', 'six cents', 'sept cents', 'huit cents', 'neuf cents'];
//     const thousands = ['', 'mille', 'deux mille', 'trois mille', 'quatre mille', 'cinq mille', 'six mille', 'sept mille', 'huit mille', 'neuf mille'];
//     const millions = ['', 'un million', 'deux millions', 'trois millions', 'quatre millions', 'cinq millions', 'six millions', 'sept millions', 'huit millions', 'neuf millions'];

//     const numStr = num.toString().padStart(9, '0'); // Pad with zeros for easier processing
//     let letterVersion = '';

//     for (let i = 0; i < 9; i += 3) {
//       const hundredsDigit = parseInt(numStr[i]);
//       const tensDigit = parseInt(numStr[i + 1]);
//       const unitsDigit = parseInt(numStr[i + 2]);

//       if (hundredsDigit > 0) {
//         letterVersion += hundreds[hundredsDigit] + ' ';
//       }

//       if (tensDigit === 1) {
//         letterVersion += teens[unitsDigit] + ' ';
//       } else {
//         if (tensDigit > 0) {
//           letterVersion += tens[tensDigit] + ' ';
//           if (unitsDigit > 0 && tensDigit !== 8) {
//             letterVersion += '-';
//           }
//         }

//         if (unitsDigit > 0 && tensDigit !== 1 && tensDigit !== 8) {
//           letterVersion += units[unitsDigit] + ' ';
//         }
//       }

//       if (i === 0 && letterVersion) {
//         letterVersion += millions[hundredsDigit] + ' ';
//       } else if (i === 3 && letterVersion) {
//         letterVersion += thousands[hundredsDigit] + ' ';
//       }
//     }

//     return letterVersion.trim();

// }


export const arrayFilter = (nail, array) => [...array].filter((item) => {
  for (let prop in item) {
    if (nail.test(item[prop])) {
      return true;
    }

  }
  return false;
});


export function toFrench3(n) {
  const units = [
    "zéro",
    "un",
    "deux",
    "trois",
    "quatre",
    "cinq",
    "six",
    "sept",
    "huit",
    "neuf",
  ];
  const teens = [
    "dix",
    "onze",
    "douze",
    "treize",
    "quatorze",
    "quinze",
    "seize",
    "dix-sept",
    "dix-huit",
    "dix-neuf",
  ];
  const tens = [
    "",
    "",
    "vingt",
    "trente",
    "quarante",
    "cinquante",
    "soixante",
    "soixante-dix",
    "quatre-vingt",
    "quatre-vingt-dix",
  ];

  if (n === 0) return "zéro";

  function helper(n) {
    if (n === 0) return "";

    if (n < 10) return units[n];

    if (n < 20) return teens[n - 10];

    const tensDigit = Math.floor(n / 10);
    const unitsDigit = n % 10;

    if (tensDigit === 7 || tensDigit === 9) {
      // Special cases for 70 and 90
      return tens[tensDigit] + (unitsDigit ? "-" + teens[unitsDigit - 1] : "");
    } else {
      return tens[tensDigit] + (unitsDigit ? "-" + units[unitsDigit] : "");
    }
  }

  const numStr = n.toString();
  const len = numStr.length;
  let words = "";

  for (let i = 0; i < len; i++) {
    const digit = parseInt(numStr[i]);
    const place = len - i - 1;

    if (digit !== 0) {
      words += helper(digit) + " ";

      if (place === 1 && digit === 1) {
        // Handle "teen" numbers
        i++;
        words = words.trim() + " ";
      } else if (place === 1) {
        words += "dix ";
      } else if (place === 2) {
        words += "cent ";
      } else if (place === 3 && digit !== 0) {
        words += "mille ";
      } else if (place === 4 && digit !== 0) {
        words += "dix mille ";
      } else if (place === 5 && digit !== 0) {
        words += "cent mille ";
      } else if (place === 6 && digit !== 0) {
        words += "million ";
      }
    }
  }

  return words.trim();
}

